import isEmpty from "lodash-es/isEmpty";
import isNumber from "lodash-es/isNumber";
import type { InteractionBlueprintData } from "@/lib/contentParsers/interactionBlueprintSchema";
import type { Metadata } from "@/lib/sparxs/metadataSchemas";
import { cloneBlueprintForRedux } from "@/lib/sparxs/cloneBlueprintForRedux";
import {
  ContentSectionType,
  CTA_TYPE_BUTTON_OVERLAY,
} from "@/types/contentSectionType";
import { FONT_SIZE } from "../../components/organisms/sections/text/TextSectionView";

type IsApplicable = (item: unknown) => boolean;

/** Content item has type field matching schema discriminant. */
const getContentType = (item: unknown): string | undefined =>
  typeof item === "object" && item !== null && "type" in item
    ? (item as { type: string }).type
    : undefined;

const isTextContent = (item: unknown): boolean =>
  getContentType(item) === ContentSectionType.Text;

const isImageContent = (item: unknown): boolean =>
  getContentType(item) === ContentSectionType.Image;

const isGridContent = (item: unknown): boolean =>
  getContentType(item) === ContentSectionType.Grid;

const isPersonalizedImageContent = (item: unknown): boolean =>
  getContentType(item) === ContentSectionType.PersonalizedImage;

/** Optional string (e.g. select) defaults: default = option 1 when missing. */
const OPTIONAL_STRING_DEFAULTS: {
  isApplicable: IsApplicable;
  key: string;
  defaultValue: string;
}[] = [
  {
    isApplicable: isTextContent,
    key: "fontSize",
    defaultValue: FONT_SIZE.REGULAR,
  },
];

/** Optional boolean defaults: default = false when missing. */
const OPTIONAL_BOOLEAN_DEFAULTS: { isApplicable: IsApplicable; key: string }[] =
  [
    { isApplicable: isImageContent, key: "primary" },
    { isApplicable: isGridContent, key: "hideBorder" },
  ];

/** Optional numeric fields may come back from the form as an empty string. */
const OPTIONAL_NUMBER_KEYS: { isApplicable: IsApplicable; key: string }[] = [
  { isApplicable: isGridContent, key: "nbColumns" },
  { isApplicable: isPersonalizedImageContent, key: "width" },
];

/** Optional string fields may come back from the form as an empty string. */
const OPTIONAL_EMPTY_STRING_KEYS = new Set(["conditionalOnVariable"]);

/** Delete a known optional key from row to satisfy no-dynamic-delete. */
const deleteKnownOptionalKey = (
  row: Record<string, unknown>,
  key: string,
): void => {
  switch (key) {
    case "fontSize":
      delete row.fontSize;
      break;
    case "primary":
      delete row.primary;
      break;
    case "hideBorder":
      delete row.hideBorder;
      break;
    case "nbColumns":
      delete row.nbColumns;
      break;
    case "width":
      delete row.width;
      break;
    case "conditionalOnVariable":
      delete row.conditionalOnVariable;
      break;
    default:
      break;
  }
};

const shouldStripOptionalNumber = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && isEmpty(value.trim())) return true;
  if (isNumber(value) && Number.isFinite(value)) return false;
  return true;
};

const stripEmptyOptionalNumbers = (item: Record<string, unknown>): void => {
  for (const entry of OPTIONAL_NUMBER_KEYS) {
    if (!entry.isApplicable(item)) continue;
    if (shouldStripOptionalNumber(item[entry.key])) {
      deleteKnownOptionalKey(item, entry.key);
    }
  }
};

const stripEmptyOptionalStrings = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach((item) => stripEmptyOptionalStrings(item));
    return;
  }
  if (typeof value !== "object" || value === null) return;

  const record = value as Record<string, unknown>;
  for (const key of OPTIONAL_EMPTY_STRING_KEYS) {
    if (isEmpty(record[key])) {
      deleteKnownOptionalKey(record, key);
    }
  }
  Object.values(record).forEach((child) => stripEmptyOptionalStrings(child));
};

/** Strip empty optional strings from table row values (satisfies no-dynamic-delete). */
const stripEmptyTableRowValues = (
  valuesRecord: Record<string, unknown>,
): void => {
  if (isEmpty(valuesRecord.name)) delete valuesRecord.name;
  if (isEmpty(valuesRecord.origin)) delete valuesRecord.origin;
  if (isEmpty(valuesRecord.facts)) delete valuesRecord.facts;
};

/** Strip empty optional strings from CTA overlay item (satisfies no-dynamic-delete). */
const stripEmptyCtaOverlayFields = (item: Record<string, unknown>): void => {
  if (isEmpty(item.domain)) delete item.domain;
  if (isEmpty(item.baseDir)) delete item.baseDir;
  if (isEmpty(item.year)) delete item.year;
  if (isEmpty(item.index)) delete item.index;
};

const stripTableRowOptionalStrings = (data: InteractionBlueprintData): void => {
  const { content } = data;
  if (!Array.isArray(content)) return;
  for (let i = 0; i < content.length; i++) {
    const item = content[i] as Record<string, unknown>;
    if (getContentType(item) !== ContentSectionType.Table) continue;
    const { rows } = item;
    if (!Array.isArray(rows)) continue;
    for (let j = 0; j < rows.length; j++) {
      const row = rows[j] as Record<string, unknown>;
      const { values } = row;
      if (typeof values !== "object" || values === null) continue;
      stripEmptyTableRowValues(values as Record<string, unknown>);
    }
  }
};

const stripCtaOverlayOptionalStrings = (
  data: InteractionBlueprintData,
): void => {
  const { content } = data;
  if (!Array.isArray(content)) return;
  for (let i = 0; i < content.length; i++) {
    const item = content[i] as Record<string, unknown>;
    if (item.type !== CTA_TYPE_BUTTON_OVERLAY) continue;
    stripEmptyCtaOverlayFields(item);
  }
};

/**
 * Deep-clone blueprint and fill known optional editor defaults only when the key
 * is missing. Backend-sent values are never overwritten. Used so editor
 * comparisons and form defaultValues see a consistent shape and ghost-only
 * diffs disappear.
 */
const DEFAULT_METADATA: Metadata = {
  description: "",
  traits: [],
  variables: [],
};

const reattachBlueprintPreserveFields = (
  blueprint: InteractionBlueprintData,
  preserved: Pick<
    InteractionBlueprintData,
    "interactionBlueprintRaw" | "metadataRaw" | "opaqueFieldPaths"
  >,
): void => {
  if (preserved.interactionBlueprintRaw !== undefined) {
    blueprint.interactionBlueprintRaw = preserved.interactionBlueprintRaw;
  }
  if (preserved.metadataRaw !== undefined) {
    blueprint.metadataRaw = preserved.metadataRaw;
  }
  if (preserved.opaqueFieldPaths !== undefined) {
    blueprint.opaqueFieldPaths = preserved.opaqueFieldPaths;
  }
};

const detachBlueprintPreserveFields = (
  blueprint: InteractionBlueprintData,
): {
  preserved: Pick<
    InteractionBlueprintData,
    "interactionBlueprintRaw" | "metadataRaw" | "opaqueFieldPaths"
  >;
  body: InteractionBlueprintData;
} => {
  const body = { ...blueprint } as InteractionBlueprintData;
  const preserved: Pick<
    InteractionBlueprintData,
    "interactionBlueprintRaw" | "metadataRaw" | "opaqueFieldPaths"
  > = {};

  if (body.interactionBlueprintRaw !== undefined) {
    preserved.interactionBlueprintRaw = body.interactionBlueprintRaw;
    delete body.interactionBlueprintRaw;
  }
  if (body.metadataRaw !== undefined) {
    preserved.metadataRaw = body.metadataRaw;
    delete body.metadataRaw;
  }
  if (body.opaqueFieldPaths !== undefined) {
    preserved.opaqueFieldPaths = body.opaqueFieldPaths;
    delete body.opaqueFieldPaths;
  }

  return { preserved, body };
};

/**
 * Deep-clone and normalize blueprint for use in the editor and Redux. Use this
 * when passing data into the form (reset), out of the form (getValues), or into
 * the session slice; the form wrapper and slice use it internally so callers
 * don't need to wrap. Also use for external blueprints (e.g. server result)
 * before comparison.
 */
export const prepareBlueprintForEditor = (
  data: InteractionBlueprintData,
): InteractionBlueprintData =>
  normalizeBlueprintForEditor(cloneBlueprintForRedux(data));

const normalizeBlueprintForEditor = (
  blueprint: InteractionBlueprintData,
): InteractionBlueprintData => {
  const cloned = JSON.parse(
    JSON.stringify(blueprint),
  ) as InteractionBlueprintData;
  const { preserved, body: out } = detachBlueprintPreserveFields(cloned);
  stripEmptyOptionalStrings(out);
  stripTableRowOptionalStrings(out);
  stripCtaOverlayOptionalStrings(out);
  if (out.metadata == null) {
    out.metadata = DEFAULT_METADATA;
  }
  if (out.content == null || !Array.isArray(out.content)) {
    out.content = [];
  }
  if (out.advisorContent === null) {
    out.advisorContent = undefined;
  }
  const { content } = out;
  for (let i = 0; i < content.length; i++) {
    const item = content[i] as Record<string, unknown>;
    stripEmptyOptionalNumbers(item);
    for (const entry of OPTIONAL_STRING_DEFAULTS) {
      if (!entry.isApplicable(item)) continue;
      if (item[entry.key] == null) {
        item[entry.key] = entry.defaultValue;
      }
    }
    for (const entry of OPTIONAL_BOOLEAN_DEFAULTS) {
      if (!entry.isApplicable(item)) continue;
      if (item[entry.key] == null) {
        item[entry.key] = false;
      }
    }
  }
  reattachBlueprintPreserveFields(out, preserved);
  return out;
};
